import Jaco3d from "../arrangement3d/Jaco3d";
import LazyPool from "./LazyPool";
import Commons from "oc-commons/Commons";
import {quat, v3} from "oc-threejs/short3";
import Maths3 from "oc-threejs/Maths3";
import Scenes from "oc-threejs/Scenes";

/**
 * Jaco ist die Basis-Klasse für alle Arrangement-Objekte
 */
export default class Jaco {

    /** @type {?Jaco} */
    parent = null;

    /** @type {?Jaco3d} */
    jaco3d;
    visible = true;
    opacity = undefined;

    // die Matrizen, wie bei Three. Unterschied: update erfolgt automatisch
    _matrixNeedsUpdate = true;
    _matrix = new THREE.Matrix4();
    _inverseMatrix = new THREE.Matrix4();

    // normalerweise der Mittelpunkt des Objektes, entspricht der jaco3d-Position, genau wie bei Three
    _position = THREE.Vector3.initWithObservation(()=>this._matrixNeedsUpdate = true);

    // die Drehung des Objektes nach der Positionierung - genau wie bei Three
    _quaternion = new THREE.Quaternion().onChange(()=>this._matrixNeedsUpdate = true);

    //------------------------------------------------------------------------------------------------------------------
    // Constructor
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Creates an instance of Jaco.
     * @param {?Jaco} parent
     */
    constructor(parent) {
        this.parent = parent;
    }

    //------------------------------------------------------------------------------------------------------------------
    // Konstruktion der Jaco-Children und Lazy Erzeugung der Jaco3D-Objekte
    //------------------------------------------------------------------------------------------------------------------

    /**
     * die wichtigste Methode - hier findet die eigentliche Konstruktion statt. 3D-Objekte können hier erstellt
     * werden, aber sie updaten sich selbst, man sollte hier nicht ihre Werte setzen!
     *
     * nicht vergessen - super.update3d() aufrufen!
     */
    update3d() {
        // alle Jaco3D - Objekte müssen mittels lazy - Methoden erzeugt werden (dadurch wird die setTouchedByJaco
        // Methode aufgerufen, wodurch ein Abräumen des entsprechenden Jaco3D verhindert wird)
        this.lazyCreateOrTouch3d(() => this.createJaco3d());
    }

    //------------------------------------------------------------------------------------------------------------------
    // Allgemeine Traversierung des ganzen Jaco - Trees
    //------------------------------------------------------------------------------------------------------------------

    /**
     * abgeleitete Klassen mit Jaco-Children sollten diese Methode überschreiben und ihre Jaco-Children iterieren
     */
    traverse(iterator) {
        iterator(this);
    }

    /**
     * benutzt traverse, um über den Jaco-Baum zu iterieren, und gibt alle Jacos zurück, auf welche predicate zutrifft
     * @param predicate
     * @return {[]}
     */
    filter(predicate) {
        const list = [];
        this.traverse(x => predicate(x) && list.push(x));
        return list;
    }

    //------------------------------------------------------------------------------------------------------------------
    // JSON Serialisierung
    //------------------------------------------------------------------------------------------------------------------

    /** */
    toJSON() {
        const json = {};
        json.position = this.position.toJSON();
        json.quaternion = this.quaternion.toJSON();
        json.box = this.getBox().toJSON();
        json.size = this.getSize().toJSON();
        return json;
    }

    /** */
    fromJSON(json) {
        if (json?.position) {
            this.position.fromJSON(json.position);
        }
        if (json?.quaternion) {
            this.quaternion.fromJSON(json.quaternion)
        }
        return this;
    }

    //------------------------------------------------------------------------------------------------------------------
    // Jaco3D erzeugen / Tree
    //------------------------------------------------------------------------------------------------------------------
    /** */
    createJaco3d() {
        return new Jaco3d(this);
    }

    /** */
    getJaco3dName() {
        return Commons.typeName(this);
    }

    // Das 3D-Object, in welches Kind-Objekte eingefügt werden. Das kann, muss aber nicht identisch mit
    // this.jaco3d sein, weil man z.B. das Koordinatensystem wechseln möchte - z.B. ein anderer Bezugspunkt, oder eine
    // Rotation
    get child3dContainer() {
        return this.jaco3d;
    }

    /** */
    lazyCreateOrTouch3d(createCallback) {
        if (this.jaco3d == null) {
            this.jaco3d = createCallback();
        }
        if (this.parent && this.jaco3d.parent==null) {
            this.parent.child3dContainer.add(this.jaco3d);
        }
        this.jaco3d.setTouchedByJaco();
        return this.jaco3d;
    }

    /** */
    lazyPool(arrayOrArrayName, jacoCreator, jaco3DCreator = null, variations = 1) {
        let array;
        // das hier aus kompatibilitätsgründen für oc-schrank
        if (_.isString(arrayOrArrayName)) {
            this[arrayOrArrayName] = array = this[arrayOrArrayName] || [];
        } else {
            array = arrayOrArrayName;
        }
        return new LazyPool(this, array, jacoCreator, jaco3DCreator, variations );
    }

    /** */
    clear3dForDebug() {
        Scenes.remove(this.jaco3d);
        this.jaco3d = null;
        APP.dirty();
    }


    //------------------------------------------------------------------------------------------------------------------
    // Size 6 Box
    //------------------------------------------------------------------------------------------------------------------

    /**
     * gibt die BoundingBox im eigenen lokalen Koordinatensystem zurück
     * @returns {THREE.Box3}
     */
    getBox() {
        return new THREE.Box3().setFromCenterAndSize(v3(),v3());
    }

    /**
     * gibt die Size im eigenen lokalen Koordinatensystem zurück
     * @returns {THREE.Vector3}
     */
    getSize() {
        return this.getBox().getSize();
    }

    /**
     * Wandelt eine Grid-Koordinate in ins lokale Koordinatensystem um.
     * Die Grid-Koordinate ist die Koordinate im Bezugssystem eines Einheitswürfels.
     * Die Eckpunkte eines Einheitsquadrats in 2D haben die Grid-Koordinaten (0,0) (1,0) (1,1) (0,1),
     * entsprechend für 3D hat die linke untere hintere Ecke die Koordinate (0,0,0) und die rechte obere vordere Ecke (1,1,1).
     * (0,0,0) wird dabei auf den this.getBox().min - Vector und (1,1,1) auf den this.getBox().max abgebildet.
     */
    grid(vectorOrX,y,z) {
        return this.getBox().grid(vectorOrX,y,z);
    }

    //------------------------------------------------------------------------------------------------------------------
    // Koordinatensystem - größtenteils wie Object3D plus ein paar Hilfsfunktionen
    // eine Scale fehlt - bisher gab es dafür keinen Anwendungsfall. Sollte eher über Size geregelt sein
    //------------------------------------------------------------------------------------------------------------------

    /**
     * damit man es nicht überschreiben kann
     */
    get position() {
        return this._position;
    }

    /**
     * damit man es nicht überschreiben kann
     */
    get quaternion() {
        return this._quaternion;
    }

    /**
     * @readonly
     * @type {THREE.Matrix4}
     */
    get matrix() {
        this._maybeUpdateMatrix();
        return this._matrix;
    }

    /**
     * @readonly
     * @type {THREE.Matrix4}
     */
    get inverseMatrix() {
        this._maybeUpdateMatrix();
        return this._inverseMatrix;
    }

    /**
     * @readonly
     * @type {THREE.Matrix4}
     */
    get matrixWorld() {
        // cachen ?
        const parents = [];
        let node = this;
        do {
            parents.push(node);
            node = node.parent;
        } while (node!=null && node.matrix!=null);
        const m = new THREE.Matrix4();
        parents.reverse().forEach(p=>m.multiply(p.matrix));
        return m;
    }

    /**
     * @returns {THREE.Matrix4}
     */
    getMatrixFromMeTo(descendant) {
        // cachen ?
        if (descendant==null || descendant===this) {
            return new THREE.Maxtrix4();
        }
        if (descendant.parent===this) {
            return descendant.matrix.clone();
        }
        const parents = [];
        let node = descendant;
        do {
            parents.push(node);
            if (node===this) {
                const m = new THREE.Matrix4();
                parents.reverse().forEach(p=>m.multiply(p.matrix));
                return m;
            }
            node = node.parent;
        } while (node!=null && node.matrix!=null);
        return new THREE.Maxtrix4();
    }

    /**
     * @param {THREE.Matrix4} matrix
     * @returns {Jaco}
     */
    applyMatrix(matrix) {
        this.matrix.multiplyMatrices(matrix, this.matrix);
        this.matrix.decompose(this.position, this.quaternion, this.scale);
        return this;
    }

    /** */
    rotateOnAxis(axis, angle) {
        const q1 = new THREE.Quaternion();
        q1.setFromAxisAngle(axis, angle);
        this.quaternion.multiply(q1);
        return this;
    }

    /** */
    _maybeUpdateMatrix() {
        if (this._matrixNeedsUpdate) {
            this._matrix.compose(this.position, this.quaternion, Maths3.vector.one);
            this._inverseMatrix.getInverse(this._matrix);
            this._matrixNeedsUpdate = false;
        }
    }

    /**
     * dummy-Methode, die nur zur API-Kompatibilität mit Object3D existiert
     * @param {?boolean} force
     */
    updateMatrixWorld(force) {
        // nichts
    }

    /**
     * @param {THREE.Vector3} axis
     * @param {number} angle
     * @returns {Virtual3D}
     */
    rotateOnAxis(axis, angle) {
        const q1 = new THREE.Quaternion();
        q1.setFromAxisAngle(axis, angle);
        this.quaternion.multiply(q1);
        return this;
    }

    /**
     * @param {THREE.Vector3} axis
     * @param {number} distance
     * @returns {Virtual3D}
     */
    translateOnAxis(axis, distance) {
        const v1 = v3();
        v1.copy(axis).applyQuaternion(this.quaternion);
        this.position.add(v1.multiplyScalar(distance));
        return this;
    }

    /**
     * @param {THREE.Vector3} vector
     * @returns {THREE.Vector3}
     */
    localToWorld(vector) {
        return vector.applyMatrix4(this.matrixWorld);
    }

    /**
     * @param {THREE.Vector3} vector
     * @returns {THREE.Vector3}
     */
    worldToLocal(vector) {
        const m1 = new THREE.Matrix4();
        return vector.applyMatrix4(m1.getInverse(this.matrixWorld));
    }

    /**
     * @param {*} [result=v3()]
     * @returns {THREE.Vector3}
     */
    getWorldPosition(result=v3()) {
        return result.setFromMatrixPosition(this.matrixWorld);
    }

    /**
     * @param {*} [result=quat()]
     * @returns {THREE.Quaternion}
     */
    getWorldQuaternion(result = quat()) {
        const position = v3();
        const scale = v3();
        this.matrixWorld.decompose(position, result, scale);
        return result;
    }

    /**
     * Gibt eine Drechachsen-Funktion zurück, die als Parameter einen Winkel entgegennimmt, um welcher das Objekt
     * gedreht wird.
     * Die Drehung gilt bei mehrfachem Aufruf absolut zur Ursprungsposition, nicht als jeweiliges Delta.
     * Die Drehachse geht durch pivotPoint (=Drehpunkt) in Richtung axis.
     * @param {THREE.Vector3} pivotPoint Punkt in lokalem Objektspace
     * @param {THREE.Vector3} axis Achse in lokalem Objektspace
     * @return {(number) => void} Drehcallback, welches einen Winkel in Bezug zur ursprünglichen Orientierung erwartet
     */
    createPivotAxis(pivotPoint, axis) {
        const translate0 = new THREE.Matrix4().makeTranslation(pivotPoint.x, pivotPoint.y, pivotPoint.z);
        const translate1 = new THREE.Matrix4().makeTranslation(-pivotPoint.x, -pivotPoint.y, -pivotPoint.z);
        const rotation = new THREE.Matrix4();
        let lastAngle = 0;
        return (angle) => {
            this.matrix
                .multiply(translate0)
                .multiply(rotation.makeRotationAxis(axis, angle - lastAngle))
                .multiply(translate1)
                .decompose(this.position, this.quaternion, this.scale);
            lastAngle = angle;
        }
    }

    /**
     * Setzt das Koordinatensystem zurück. Das ist bei Objekten, die wiederverwendet werden und nur per Konstruktion erzeugt werden,
     * sinnvoll und sollte vom eigenen update3d oder dem des Parents aus aufgerufen werden.
     * @returns {Jaco}
     */
    resetCoordinates() {
        this.position.set(0,0,0);
        this.quaternion.set(0,0,0,1);
        return this;
    }

}
